import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { PolicyDetailsPersonalAutoBodyTypeClient, PolicyDetailsPersonalAutoBodyTypeModel, PolicyDetailsPersonalAutoBodyTypeRefModel } from '../../../../../client/ApiClient';
import { DataCell, DataRow, DataTable } from '../../../../basic/DataTable';
import { RangePaginationState, RangePagination, paginate } from 'components/basic/Pagination';
import StageActionToolbar from '../generic/StageActionToolbar';
import { DropdownMenu, DropdownToggle, UncontrolledDropdown, DropdownItem } from 'reactstrap';
import { stagingPageUpdate, stagingPageCopyMappings, StagingPageCopyMappingsItem } from '../generic/Abstractions'
import { RowStatus, AssignRowStatus } from '../generic';
import { filterColumns, sort } from '../generic/EntityFunctions';

import '../../../../../styles/Icons.scss';

interface PolicyDetailsPersonalAutoBodyTypeStagingPageArgs {
    conversionId: string,
}

type PolicyDetailsPersonalAutoBodyTypeStagingPageProps = RouteComponentProps<PolicyDetailsPersonalAutoBodyTypeStagingPageArgs>;

interface PolicyDetailsPersonalAutoBodyTypeStagingPageState {
    items: (PolicyDetailsPersonalAutoBodyTypeModel
        & StagingPageCopyMappingsItem)[];
    destinationCodes: PolicyDetailsPersonalAutoBodyTypeRefModel[];
    filter: string;
    sort: boolean;
    sortColumn: string;
    mapped: boolean;
    notMapped: boolean;
    lastValue: string;
    pagination: RangePaginationState;
    buttonLock: boolean;
    filteredColumn: string;
}

export class PolicyDetailsPersonalAutoBodyTypeStagingPage extends React.Component<PolicyDetailsPersonalAutoBodyTypeStagingPageProps, PolicyDetailsPersonalAutoBodyTypeStagingPageState>{
    private client: PolicyDetailsPersonalAutoBodyTypeClient = new PolicyDetailsPersonalAutoBodyTypeClient()

    private readonly copyMappings = stagingPageCopyMappings(this);

    constructor(props: PolicyDetailsPersonalAutoBodyTypeStagingPageProps) {
        super(props)
        this.state = {
            items: [],
            destinationCodes: [],
            filter: '',
            sort: true,
            sortColumn: '',
            mapped: true,
            notMapped: true,
            lastValue:'',
            pagination: {
                pageSize: 25,
                pageNumber: 0,
            },
            buttonLock: true,
            filteredColumn: '',
        }
    }

    componentDidMount() {
        this.getItems();
    }

    setDefaults = (entity: PolicyDetailsPersonalAutoBodyTypeModel) =>
        new PolicyDetailsPersonalAutoBodyTypeModel({
            ...entity,
            referenceId: entity.referenceId || '',
            destinationCode: this.destinationName(entity.referenceId, true),
            destinationDescription: this.destinationName(entity.referenceId, false)
        })

    getItems = () => {
        const items = this.client.getAll(this.props.match.params.conversionId, '')
        const refs = this.client.getAllRefs(this.props.match.params.conversionId)
        Promise.all([items, refs])
            .then(result => {
                this.setState({
                    items: result[0]!,
                    destinationCodes: result[1]!
                }, async () => {
                    if (await items && await refs) {
                        this.setState({
                            items: sort(this.state.items.map(this.setDefaults), this.state.sortColumn, this.state.sort, []),
                        });
                    }
                })

            })
            .catch(error => { });
    }

    put(entity: PolicyDetailsPersonalAutoBodyTypeModel) {
        if (entity.referenceId === '') {
            entity.referenceId = undefined
        }
        stagingPageUpdate(this, this.client)
            .withSource('Personal Auto Body Type', entity.sourceCode!)
            .update(entity)
            .catch(() => { });
    }

    destinationName = (refId: string | undefined, type: boolean) => {
        if (refId) {
            const destination = this.state.destinationCodes.filter(ref => ref.id === refId);
            if (destination[0]) { return type ? destination[0].code : destination[0].description }
            else { return '' }
        } else {
            return ''
        }
    }

    handleSearch = (filter: string) => {
        this.setState({ filter })
    }

    handleChange = (e: any) => {
        const dc = e.target.value;
        const id = e.target.name;

        const items = this.state.items;
        const i = this.state.items.findIndex(v => v.id === id);
        items[i].destinationDescription = this.destinationName(dc, false)
        items[i].referenceId = dc;

        this.setState({ items });
    }

    sort(column: string) {
        const sorted = this.state.sortColumn === column ? !this.state.sort : true;
        const items = sort(this.state.items, column, sorted, [])
        this.setState({ sort: sorted, sortColumn: column, items });
    }

    mappedCheck(check: string) {
        if (check === 'notMapped') {
            const notMapped = !this.state.notMapped
            this.setState({ notMapped })
        } else if (check === 'mapped') {
            const mapped = !this.state.mapped
            this.setState({ mapped })
        }
    }

    lockButton = (lock: boolean) => {
        this.setState({ buttonLock: lock })
    }

    selectFilteredColumn = (filteredColumn: string) => {
        this.setState({ filteredColumn })
    }

    public render() {
        const conversionId = this.props.match.params.conversionId;
        const filteredColumn = this.state.filteredColumn
        const items = this.state.items
            .map(AssignRowStatus)
            .filter(i => ((filteredColumn === '' || filteredColumn === 'sourceCode') && i.sourceCode && i.sourceCode!.toLowerCase().includes(this.state.filter.trim().toLowerCase())) ||
                ((filteredColumn === '' || filteredColumn === 'sourceDescription') && i.sourceDescription && i.sourceDescription!.toLowerCase().includes(this.state.filter.trim().toLowerCase())) ||
                ((filteredColumn === '' || filteredColumn === 'destinationCode') && i.destinationCode && i.destinationCode!.toLowerCase().includes(this.state.filter.trim().toLowerCase())) ||
                ((filteredColumn === '' || filteredColumn === 'destinationDescription') && i.destinationDescription && i.destinationDescription!.toLowerCase().includes(this.state.filter.trim().toLowerCase())))
            .filter(item =>
                (item.mapped === RowStatus.NotMapped && this.state.notMapped)
                || (item.mapped === RowStatus.Mapped && this.state.mapped));

        return (
            <>
                <StageActionToolbar
                    conversionId={conversionId}
                    sectionId='personalauto'
                    sectionType='bodytype'
                    handleSearch={this.handleSearch}
                    buttonText='Migrate All Personal Auto'
                    handleCopyMapping={this.copyMappings.submitMappings}
                    tierGate='tier4'
                    copyMap={true}
                    getItems={() => this.getItems()}
                    filter={this.state.filter}
                    lockButton={this.lockButton}
                    filterColumns={filterColumns(items)}
                    selectFilteredColumn={this.selectFilteredColumn}
              />
                <DataTable>
                    <thead>
                        <tr className='nonFullWidthRow'>
                            <th onClick={() => this.sort('sourceCode')}>Source Code<i className="fa fa-sort"></i></th>
                            <th onClick={() => this.sort('sourceDescription')}>Source Description<i className="fa fa-sort"></i></th>
                            <th onClick={() => this.sort('destinationCode')}>Destination Code<i className="fa fa-sort"></i></th>
                            <th className='checkbox-column'>
                                <UncontrolledDropdown setActiveFromChild className="ismappedDropdown">
                                    <DropdownToggle tag="a" className="ismapped" caret>
                                        Mapped
                                 </DropdownToggle>
                                    <DropdownMenu>
                                        <DropdownItem className={!this.state.notMapped ? 'mappedChecked' : ''} onClick={() => this.mappedCheck('notMapped')}>&#x274C; Not Mapped</DropdownItem>
                                        <DropdownItem className={!this.state.mapped ? 'mappedChecked' : ''} onClick={() => this.mappedCheck('mapped')}>&#x2705; Mapped</DropdownItem>
                                    </DropdownMenu>
                                </UncontrolledDropdown>
                            </th>
                            <th onClick={() => this.sort('isUserMapped')} className='checkbox-column'>Auto-Mapped<i className="fa fa-sort"></i></th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            paginate(items, this.state.pagination, (item, index) => (
                                <DataRow id={item.id.toString()} key={item.id}>
                                    <DataCell>{item.sourceCode}</DataCell>
                                    <DataCell>{item.sourceDescription}</DataCell>
                                    <DataCell>
                                        <select className="custom-select" value={item.referenceId} name={item.id.toString()} disabled={this.state.buttonLock} onChange={this.handleChange} onFocus={(e) => this.setState({ lastValue: e.target.value.trim() })} onBlur={() => { if (this.state.lastValue !== item.referenceId) this.put(PolicyDetailsPersonalAutoBodyTypeModel.fromJS(item)) }}>
                                            <option value=''>Select Destination</option>
                                            {this.state.destinationCodes
                                                .map((dc, i) =>
                                                    <option key={i} value={dc.id}> {dc.code} - {dc.description}</option>
                                                )}
                                        </select>
                                    </DataCell>
                                    <DataCell className='checkbox-column' itemProp={item.mapped === RowStatus.NotMapped ? 'unmapped' : item.mapped === RowStatus.Mapped ? 'mapped' : 'dropped'}>
                                        {item.mapped === RowStatus.NotMapped ? < span className='mapped' itemProp='unmapped'>&#x274C;</span> : item.mapped === RowStatus.Mapped ? < span className='mapped' itemProp='mapped'>&#x2705;</span> : < span className='mapped' itemProp='dropped'>&#x2796;</span>}
                                    </DataCell>
                                    <DataCell
                                        className='checkbox-column'
                                        itemProp={!item.isUserMapped && item.isMapped ? 'auto-mapped' : ''}
                                    >
                                        {!item.isUserMapped && item.isMapped ? < span className='mapped' itemProp='auto-mapped'>&#x2705;</span> : ''}
                                    </DataCell>
                                </DataRow>
                            ))
                        }
                    </tbody>
                </DataTable>
                <RangePagination
                    pageNumber={this.state.pagination.pageNumber}
                    pageSize={this.state.pagination.pageSize}
                    recordsCount={items.length}
                    onChangeIndex={index => this.setState({
                        pagination: {
                            ...this.state.pagination,
                            pageNumber: index,
                        }
                    })}
                />
            </>
        );
    }
}